import React, {useContext, useState} from "react";
import {
    Button,
    Stack,
    Toolbar,
    Typography,
    AppBar,
    IconButton,
    Tooltip,
    Menu,
    Box,
    CircularProgress, ListItemButton, List, ListItemIcon, ListItemText
} from "@mui/material";
import {Link} from "react-router-dom";
import {
    IconChecklist,
    IconClipboardCopy,
    IconCloudUpload, IconDeviceFloppy, IconFile, IconFileExport,
    IconMenu, IconPlayerPauseFilled, IconPlayerPlay,
} from "@tabler/icons-react";
import {Context as AppContext, ProjectRoles} from "../../../context/AppContext";
import {grey} from "@mui/material/colors";
import {HeaderDrawer} from "./HeaderDrawer";
import {toast} from "react-toastify";
import {copyToClipboard} from "../../../utils/functions";
import {FlowProjectRepository} from "../../../repositories/FlowProjectRepository";
import axios from "axios";
import useConfirm from "../../../hooks/useConfirm";
import {SignInContext} from "../../../context/SignInContext";
import {NotificationButton} from "./notification-button";
import {DialButton} from "../../../pages/project/supervision-page/components/dial-button";

const checkNodesValid = (project) => {
    // TODO: implement real validation of node.data
    return true;
};


const segmentSx = {
    minWidth: 0,
    px: 1.75,
    height: 36,
    borderRadius: 0,
    color: "text.secondary",
    fontSize: 13,
    fontWeight: 500,
};

export const Header = ({project, openChat, toggleChat}) => {
    const {publishVersion} = useContext(AppContext);
    const {currentUser} = useContext(SignInContext);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lastCheckPassed, setLastCheckPassed] = useState(true);
    const confirmDeploy = useConfirm(
        'Are you sure you want to deploy to production?',
    );

    const onCheck = () => {
        const isValid = checkNodesValid(project);
        setLastCheckPassed(isValid);
        if (isValid) {
            toast.success("All nodes passed validation.");
        } else {
            toast.error("Some nodes have validation errors.");
        }
        return isValid;
    }

    const onPublish = async () => {
        if (!checkNodesValid(project)) {
            setLastCheckPassed(false);
            toast.error("Fix validation errors before saving.");
            return;
        }
        try {
            setLoading(true);
            await publishVersion(project);
            const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
            const projectVersionResponse = await axios.get(fetchVersion)
            await FlowProjectRepository.updateFlow({id: project?.id, project: projectVersionResponse?.data || null})
        } catch (e) {
            console.log(e);
            toast.error("Error! Please try again.")
        } finally {
            setLoading(false);
        }
    }

    const onDeploy = async () => {
        if (!project?.settings?.deploymentProject) {
            return;
        }
        if (!checkNodesValid(project)) {
            setLastCheckPassed(false);
            toast.error("Fix validation errors before deploying.");
            return;
        }
        const result = await confirmDeploy();
        if (!result) {
            return;
        }
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/deployProject`
            const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
            const response = await axios.post(url, {
                fromProjectId: project?.id,
                toProjectId: project?.settings?.deploymentProject,
            })

            const projectVersionResponse = await axios.get(fetchVersion)
            if (response?.data?.project || response?.project) {
                const newProject = response?.data?.project || response?.project;
                await publishVersion(newProject || null);
                await FlowProjectRepository.updateProjectFromStage({id: project?.settings?.deploymentProject, url: newProject?.configuration?.conversation_logger_api || null, project: projectVersionResponse?.data || null})
            }
        } catch (e) {
            console.log(e);
            toast.error("Error! Please try again.")
        } finally {
            setLoading(false);
        }
    }

    const userRole = currentUser?.roles?.includes("admin") ? ProjectRoles.ADMIN : project?.members?.find(m => m?.username === currentUser?.username)?.role;

    return (
        <>
            <HeaderDrawer
                open={openDrawer}
                handleClose={() => setOpenDrawer(false)}
                openCart={() => {
                    setOpenDrawer(false);
                }}
            />
            <AppBar position="static"
                    sx={{
                        bgcolor: "#fefefe",
                        boxShadow: 0,
                        zIndex: 100,
                        borderBottom: 1,
                        minHeight: "48px !important",
                        borderColor: "divider"
                    }}
            >
                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        // display: "none",
                        zIndex: 100,
                        py: "0 !important",
                        px: "8px !important",
                        minHeight: "48px !important",
                    }}
                >
                    <Box sx={{width: "100%", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 2}}>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <Button
                                component={Link}
                                to={"/projects"}
                                variant={"ghost"}
                                sx={{flexDirection: "row", color: "dark.main"}}
                                startIcon={<img src={"/logo.svg"} alt={"logo"} width={30}/>}
                            >
                                <Typography fontWeight={700}>BlockFlow</Typography>
                            </Button>
                        </Stack>

                        <Stack direction={"row"} gap={0.75} alignItems={"baseline"} sx={{justifySelf: "center"}}>
                            <Typography fontSize={13} color="text.disabled">Project</Typography>
                            <Typography fontSize={13} color="text.disabled">·</Typography>
                            <Typography fontSize={14} fontWeight={500} color="text.secondary">{project?.name}</Typography>
                            {/* Dirty-state indicator — wire this to real "has unsaved
                  changes" state once that's tracked; purely decorative
                  for now. */}
                            {/* <Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "warning.main"}} /> */}
                        </Stack>

                        <Stack direction={"row"} gap={3} alignItems={"center"} sx={{justifySelf: "end"}}>
                            {/*<DialButton project={project} />*/}
                            {
                                userRole !== ProjectRoles.AGENT ?
                                    <>
                                        <ExportProjectButton
                                            project={project}
                                            showIconText={false}
                                            sx={{color: "text.secondary", minWidth: 40, width: 36, height: 36, borderRadius: 1}}
                                        />

                                        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                            <Tooltip title="Validate all node data before saving">
                                                <Button
                                                    variant={"ghost"}
                                                    color={"secondary"}
                                                    disabled={!project || loading}
                                                    onClick={onCheck}
                                                    startIcon={<IconChecklist size={16}/>}
                                                    sx={{
                                                        ...segmentSx,
                                                        position: "relative",
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    Validate
                                                    <Box sx={{
                                                        position: "absolute", top: 6, right: 6,
                                                        width: 6, height: 6, borderRadius: "50%",
                                                        bgcolor: lastCheckPassed ? "success.main" : "error.main"
                                                    }}/>
                                                </Button>
                                            </Tooltip>
                                            <Button
                                                variant={"ghost"}
                                                color={"secondary"}
                                                disabled={!project || loading}
                                                onClick={onPublish}
                                                startIcon={loading ?
                                                    <CircularProgress size={16} sx={{color: grey[700]}}/> :
                                                    <IconDeviceFloppy size={16}/>}
                                                sx={{...segmentSx, borderRadius: 1}}
                                            >
                                                Save
                                            </Button>
                        <span>
                          <Button
                              variant={"ghost"}
                              color={"secondary"}
                              disabled={!project || !project?.settings?.deploymentProject || loading}
                              onClick={onDeploy}
                              startIcon={loading ?
                                  <CircularProgress size={16} sx={{color: grey[700]}}/> :
                                  <IconCloudUpload size={16}/>}
                              sx={{...segmentSx, borderRadius: 1}}
                          >
                            Deploy
                          </Button>
                        </span>

                                            <NotificationButton project={project}/>
                                        </Stack>
                                    </> : null
                            }
                            {/*<DialButton project={project} />*/}
                            {userRole === ProjectRoles.AGENT && <NotificationButton project={project}/>}
                            <Button
                                variant={"primary"}
                                color={"primary"} size={"small"}
                                disabled={!project || loading}
                                onClick={toggleChat}
                                startIcon={openChat ? <IconPlayerPauseFilled size={18}/> : <IconPlayerPlay size={18}/>}
                                sx={{
                                    borderRadius: "8px", minWidth: 40, boxShadow: 0, px: 2,
                                }}
                            >
                                {openChat ? "Close" : "Start"}
                            </Button>
                        </Stack>
                    </Box>
                </Toolbar>

                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        my: 1,
                        display: "none"
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={2} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <Button
                            component={Link}
                            to={"/projects"}
                            variant={"ghost"}
                            sx={{color: "#252525", flexDirection: "column"}}
                        >
                            <img src={"/logo.svg"} alt={"logo"} width={50}/>
                            {/*<Typography fontSize={13} fontColor={"#252525"} fontWeight={"bold"}>BlockFlow</Typography>*/}
                        </Button>

                        <IconButton onClick={() => setOpenDrawer(true)}>
                            <IconMenu/>
                        </IconButton>
                    </Stack>
                </Toolbar>
            </AppBar>
        </>
    )
}


export const ExportProjectButton = ({project, showIconText = false, ...props}) => {
    const [anchorElUser, setAnchorElUser] = React.useState(null);

    const handleOpenUserMenu = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorElUser(null);
    };

    const onCopyExport = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        if (project.id) {
            try {
                const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
                await copyToClipboard(url);
                toast.success("URL is copied successfully.")
            } catch (e) {
                console.log(e)
                toast.error("URL failed to copy.")
            }
        }

        handleCloseUserMenu(event);
    }
    const onExportFile = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        // Replace with the actual API endpoint that serves the file
        const apiUrl = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/export/${project?.id}`;

        fetch(apiUrl)
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);

                // Create an anchor element to trigger the download
                const a = document.createElement('a');
                a.href = url;
                a.download = `${project.id}.json`; // Specify the desired file name
                document.body.appendChild(a);
                a.click();

                // Clean up by removing the anchor and the URL
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error('Error downloading file: ', error);
            });

        handleCloseUserMenu(event);
    };


    return (
        <>
            {
                showIconText ?
                    <Tooltip title="Export">
                        <Button
                            onClick={handleOpenUserMenu}
                            color={"secondary"}
                            startIcon={
                                showIconText ? <IconFileExport size={18} stroke={1.5}/> : null
                            }
                            {...props}
                        >
                            Export
                        </Button>
                    </Tooltip> :
                    <Tooltip title="Export">
                        <IconButton
                            onClick={handleOpenUserMenu}
                            {...props}
                        >
                            <IconFileExport size={17} stroke={1.5}/>
                        </IconButton>
                    </Tooltip>
            }

            <Menu
                component={List}
                // sx={{ p: 0, borderRadius: 0, minWidth: "200px"}}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            p: 1, borderRadius: 3,
                        }
                    }
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <ListItemButton onClick={onCopyExport}>
                    <ListItemIcon><IconClipboardCopy/></ListItemIcon>
                    <ListItemText primary={"Copy URL"}/>
                </ListItemButton>
                <ListItemButton onClick={onExportFile}>
                    <ListItemIcon><IconFile/></ListItemIcon>
                    <ListItemText primary={"Download File"}/>
                </ListItemButton>
            </Menu>
        </>
    )
}